import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useForm } from "react-hook-form";
import { Loader2, AlertCircle, Phone } from "lucide-react";
import {
  getListingById,
  updateListing,
} from "@/lib/actions";
import { getCategories, getSubcategories } from "@/lib/actions";
import { useAuth } from "@/context/AuthContext";
import { uploadProfileAssetToStorage } from "@/integrations/supabase/storage";
import { useScrollTop } from "@/hooks/useScrollTop";
import AIDescriptionButton from "@/components/listings/AIDescriptionButton";
import AITitleButton from "@/components/listings/AITitleButton";
import imageCompression from "browser-image-compression";
import { Checkbox } from "@/components/ui/checkbox";
import CitySelect from "@/components/ui/CitySelect";
import VideoUploader from "@/components/listings/VideoUploader";
import { Progress } from "@/components/ui/progress";

const formSchema = z.object({
  title: z.string().min(3, {
    message: "Titulli duhet të ketë të paktën 3 shkronja.",
  }).max(50, {
    message: "Titulli nuk mund të jetë më i gjatë se 50 shkronja.",
  }),
  description: z.string().min(10, {
    message: "Përshkrimi duhet të ketë të paktën 10 shkronja.",
  }),
  price: z
    .string()
    .refine(
      (value) => {
        try {
          const num = parseFloat(value);
          return !isNaN(num) && num > 0;
        } catch (e) {
          return false;
        }
      },
      {
        message: "Çmimi duhet të jetë një numër i vlefshëm më i madh se 0.",
      }
    ),
  isPriceNegotiable: z.boolean().optional(),
  category: z.string().min(1, {
    message: "Ju lutemi zgjidhni një kategori.",
  }),
  subcategory: z.string().min(1, {
    message: "Ju lutemi zgjidhni një nënkategori.",
  }),
  condition: z.string().optional(),
  country: z.string().min(1, {
    message: "Ju lutemi zgjidhni shtetin.",
  }),
  location: z.string().min(3, {
    message: "Qyteti duhet të zgjidhet.",
  }),
  deliveryOption: z.string().min(1, {
    message: "Ju lutemi zgjidhni opsionin e dorëzimit.",
  }),
  shippingCost: z.string().optional(),
  stock: z.string().refine(
    (value) => {
      try {
        const num = parseInt(value, 10);
        return !isNaN(num) && num >= 0;
      } catch (e) {
        return false;
      }
    },
    {
      message:
        "Stoku duhet të jetë një numër i plotë i vlefshëm më i madh ose baraz me 0.",
    }
  ),
  phone: z.string().min(6, {
    message: "Numri i telefonit duhet të ketë të paktën 6 shifra."
  }).max(15, {
    message: "Numri i telefonit nuk mund të ketë më shumë se 15 shifra."
  }),
  phoneCountryCode: z.string().optional(),
  whatsappEnabled: z.boolean().optional(),
  viberEnabled: z.boolean().optional(),
});

const kosovoCities = [
  "Pristina","Prizren","Gjilan","Ferizaj","Gjakova","Peja","Fushë Kosova",
  "Matiçan","Mitrovica","Vushtrri","Podujeva","Rahovec","Suhareka","Kaçanik",
  "Kamenica","Shtime","Lipjan","Obiliq","Skënderaj","Drenas","Klina",
  "Leposavić","Deçan","Dragash","Istog","Malisheva","Junik","Hani i Elezit",
  "Viti","Mamusha","Zubin Potok","Zvečan","Štrpce","Graçanica","Novo Brdo",
  "Klokot","Parteš","Ranilug"
];

const albaniaCities = [
  "Tirana", "Durrës", "Vlorë", "Shkodër", "Elbasan", "Fier", "Korçë", "Berat",
  "Lushnjë", "Kukës", "Lezhë", "Pogradec", "Gjirokastër", "Sarandë", "Kavajë",
  "Laç", "Patos", "Tepelenë", "Librazhd", "Krujë", "Gramsh", "Përmet", "Bulqizë",
  "Burrel", "Rrëshen", "Cërrik", "Peshkopi", "Ballsh", "Ersekë", "Divjakë", "Delvinë"
];

const MAX_IMAGES = 5;
const MAX_VIDEO_SIZE_MB = 20;

const EditListing = () => {
  useScrollTop();

  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [images, setImages] = useState<File[]>([]);
  const [uploadedImageUrls, setUploadedImageUrls] = useState<string[]>([]);

  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [currentVideoUrl, setCurrentVideoUrl] = useState<string | null>(null);
  
  const [isUploadingAssets, setIsUploadingAssets] = useState(false);
  const [assetUploadProgress, setAssetUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const [categoriesList, setCategoriesList] = useState<any[]>([]);
  const [subcategoriesList, setSubcategoriesList] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<number | null>(null);

  const [isServiceCategory, setIsServiceCategory] = useState(false);
  const [isJobCategory, setIsJobCategory] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      description: "",
      price: "",
      isPriceNegotiable: false,
      category: "",
      subcategory: "",
      condition: "",
      country: "",
      location: "",
      deliveryOption: "",
      shippingCost: "",
      stock: "",
      phone: "",
      phoneCountryCode: "+355",
      whatsappEnabled: false,
      viberEnabled: false,
    },
  });

  const { isLoading, error, data: listing } = useQuery({
    queryKey: ["listing", id],
    queryFn: async () => {
      if (!id) return null;
      const listingId = parseInt(id, 10);
      return await getListingById(listingId);
    },
    enabled: !!id,
  });

  const { isLoading: categoriesLoading } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      const categories = await getCategories();
      setCategoriesList(categories);
      return categories;
    },
  });

  const { isLoading: subcategoriesLoading } = useQuery({
    queryKey: ["subcategories", selectedCategory],
    queryFn: async () => {
      if (!selectedCategory) return [];
      const subcategories = await getSubcategories(selectedCategory);
      setSubcategoriesList(subcategories);
      return subcategories;
    },
    enabled: !!selectedCategory,
  });

  useEffect(() => {
    if (selectedCategory) {
      const categoryId = selectedCategory.toString();
      const newIsServiceCategory = ['8', '9', '10'].includes(categoryId);
      const newIsJobCategory = categoryId === '9';
      
      setIsServiceCategory(newIsServiceCategory);
      setIsJobCategory(newIsJobCategory);
      
      if (newIsServiceCategory && !form.getValues('condition')) {
        form.setValue('condition', 'new');
      }
      
      if (newIsServiceCategory && form.getValues('deliveryOption') !== 'pickup_only') {
        form.setValue('deliveryOption', 'pickup_only');
      }
      
      if (newIsJobCategory && form.getValues('stock') !== '1') {
        form.setValue('stock', '1');
      }
    }
  }, [selectedCategory, form]);

  useEffect(() => {
    if (listing) {
      form.reset({
        title: listing.title,
        description: listing.description,
        price: listing.price.toString(),
        isPriceNegotiable: listing.is_price_negotiable || false,
        category: listing.category_id.toString(),
        subcategory: listing.subcategory_id.toString(),
        condition: listing.condition,
        country: listing.country,
        location: listing.location,
        deliveryOption: listing.delivery_option,
        shippingCost: listing.shipping_cost ? listing.shipping_cost.toString() : "",
        stock: listing.stock.toString(),
        phone: listing.phone || "", 
        phoneCountryCode: listing.phone_country_code || "+355",
        whatsappEnabled: listing.whatsapp_enabled || false,
        viberEnabled: listing.viber_enabled || false,
      });
      
      setUploadedImageUrls(listing.images || []);
      setCurrentVideoUrl(listing.video_url || null);
      setSelectedCategory(listing.category_id);
      setSelectedSubcategory(listing.subcategory_id);
      
      const categoryId = listing.category_id.toString();
      setIsServiceCategory(['8', '9', '10'].includes(categoryId));
      setIsJobCategory(categoryId === '9');
    }
  }, [listing, form]);

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const selectedFiles = Array.from(e.target.files);
    const currentCount = uploadedImageUrls.length + images.length;
    const allowedCount = MAX_IMAGES - currentCount;
    if (allowedCount <= 0) {
      setUploadError("Ju keni arritur numrin maksimal të fotove (5).");
      return;
    }
    const imageFiles = selectedFiles.filter((file) => file.type.startsWith("image/"));
    if (imageFiles.length > allowedCount) {
      setUploadError(`Ju lejohet të ngarkoni vetëm ${allowedCount} foto të tjera.`);
      imageFiles.splice(allowedCount);
    } else {
      setUploadError(null);
    }

    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 2000,
      useWebWorker: true,
    };
    const compressedFiles: File[] = [];
    for (const file of imageFiles) {
      try {
        const compressed = await imageCompression(file, options);
        compressedFiles.push(compressed);
      } catch (err) {
        compressedFiles.push(file);
      }
    }
    setImages((prev) => [...prev, ...compressedFiles]);
  };

  const removeImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const removeUploadedImage = (index: number) => {
    setUploadedImageUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCategoryChange = (value: string) => {
    setSelectedCategory(parseInt(value, 10));
    form.setValue("category", value);
    form.setValue("subcategory", "");
    
    const newIsServiceCategory = ['8', '9', '10'].includes(value);
    const newIsJobCategory = value === '9';
    
    setIsServiceCategory(newIsServiceCategory);
    setIsJobCategory(newIsJobCategory);
    
    if (newIsServiceCategory) {
      form.setValue('condition', 'new');
      form.setValue('deliveryOption', 'pickup_only');
    }
    
    if (newIsJobCategory) {
      form.setValue('stock', '1');
    }
  };

  const handleSubcategoryChange = (value: string) => {
    setSelectedSubcategory(parseInt(value, 10));
    form.setValue("subcategory", value);
  };

  const handleAIDescription = (description: string) => {
    form.setValue("description", description);
  };

  const handleAITitle = (title: string) => {
    form.setValue("title", title);
  };

  const handleRemoveCurrentVideo = useCallback(() => {
    setCurrentVideoUrl(null);
    toast({ title: "Videoja u hoq", description: "Videoja ekzistuese do të hiqet kur të ruani ndryshimet."});
  }, [toast]);

  const uploadAssets = async (): Promise<{ finalImageUrls: string[], finalVideoUrl: string | null }> => {
    if (!user?.id) {
      setUploadError("Përdoruesi nuk është i autentikuar për ngarkim.");
      throw new Error("Përdoruesi nuk është i autentikuar");
    }

    setIsUploadingAssets(true);
    setAssetUploadProgress(0);
    setUploadError(null);
    let newImageUrls: string[] = [];
    let newVideoUrl: string | null = null;
    const totalFilesToUpload = images.length + (videoFile ? 1 : 0);
    let filesUploaded = 0;

    const updateOverallProgress = () => {
      if (totalFilesToUpload > 0) {
        setAssetUploadProgress(Math.round((filesUploaded / totalFilesToUpload) * 100));
      }
    };

    for (const file of images) {
      try {
        const downloadURL = await uploadProfileAssetToStorage(file, user.id, 'listing_image', true);
        newImageUrls.push(downloadURL);
        filesUploaded++;
        updateOverallProgress();
      } catch (err) {
        setUploadError("Gabim gjatë ngarkimit të një imazhi. Ju lutemi provoni përsëri.");
        setIsUploadingAssets(false);
        throw new Error("Ngarkimi i imazheve dështoi");
      }
    }

    if (videoFile) {
      try {
        newVideoUrl = await uploadProfileAssetToStorage(videoFile, user.id, 'listing_video', true);
        filesUploaded++;
        updateOverallProgress();
      } catch (err) {
        setUploadError("Gabim gjatë ngarkimit të videos. Ju lutemi provoni përsëri.");
        setIsUploadingAssets(false);
        throw new Error("Ngarkimi i videos dështoi");
      }
    }
    
    setIsUploadingAssets(false);
    const finalImageUrls = [...uploadedImageUrls, ...newImageUrls];
    const finalVideoUrl = newVideoUrl || currentVideoUrl;

    return { finalImageUrls, finalVideoUrl };
  };

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    if (!user) {
      toast({
        title: "Gabim",
        description: "Ju duhet të jeni të identifikuar për të përditësuar shpalljen",
        variant: "destructive",
      });
      return;
    }

    try {
      setUploadError(null);
      if (isServiceCategory) {
        form.clearErrors('condition');
      }
      if (isJobCategory) {
        form.clearErrors('stock');
      }
      
      const isValid = await form.trigger();
      if (!isValid) {
        toast({
          title: "Gabim",
          description: "Ju lutemi plotësoni të gjitha fushat e kërkuara",
          variant: "destructive",
        });
        return;
      }
      
      const { finalImageUrls, finalVideoUrl } = await uploadAssets();

      const listingData = {
        title: values.title,
        description: values.description,
        price: parseFloat(values.price),
        is_price_negotiable: values.isPriceNegotiable,
        currency: "EUR",
        category_id: parseInt(values.category, 10),
        subcategory_id: parseInt(values.subcategory, 10),
        condition: isServiceCategory ? 'new' : values.condition,
        country: values.country,
        location: values.location,
        images: finalImageUrls, 
        video_url: finalVideoUrl,
        delivery_option: isServiceCategory ? 'pickup_only' : values.deliveryOption,
        stock: isJobCategory ? 1 : parseInt(values.stock, 10),
        shipping_cost: values.shippingCost ? parseFloat(values.shippingCost) : null,
        is_active: false, 
        approval_status: "pending",
        phone: values.phone,
        phone_country_code: values.phoneCountryCode,
        whatsapp_enabled: values.whatsappEnabled,
        viber_enabled: values.viberEnabled,
      };

      if (!id) {
        toast({
          title: "Gabim",
          description: "ID e shpalljes nuk u gjet",
          variant: "destructive",
        });
        return;
      }

      const listingIdNum = parseInt(id, 10);
      const success = await updateListing(listingIdNum, listingData);

      if (success) {
        toast({
          title: "Sukses",
          description: "Shpallja u përditësua me sukses!",
        });
        navigate(`/listing/${id}`);
      } else {
        toast({
          title: "Gabim",
          description: "Gabim gjatë përditësimit të shpalljes",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      const message = uploadError || error.message || "Gabim gjatë përditësimit të shpalljes";
      toast({
        title: "Gabim",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsUploadingAssets(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-8">
        <AlertCircle className="mx-auto h-12 w-12 text-red-400" />
        <p className="mt-4 text-red-500">Gabim gjatë ngarkimit të shpalljes</p>
      </div>
    );
  }

  if (!listing) {
    return (
      <div className="text-center p-8">
        <p className="text-gray-500">Shpallja nuk u gjet.</p>
      </div>
    );
  }

  const userIdLocal = user?.id;

  if (!userIdLocal) {
    return (
      <div className="text-center p-8">
        <AlertCircle className="mx-auto h-12 w-12 text-red-400" />
        <p className="mt-4 text-red-500">
          Ju duhet të jeni të loguar për të përditësuar shpalljen
        </p>
      </div>
    );
  }

  return (
    <div className="container py-8">
      <h1 className="text-2xl font-bold mb-4">Përditëso Shpalljen</h1>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Titulli</FormLabel>
                <FormControl>
                  <Input placeholder="Titulli" {...field} maxLength={50} />
                </FormControl>
                <FormMessage />
                <AITitleButton
                  description={form.getValues("description")}
                  category={form.getValues("category")}
                  subcategory={form.getValues("subcategory")}
                  condition={form.getValues("condition")}
                  onGeneratedTitle={handleAITitle}
                />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Numri i telefonit</FormLabel>
                <div className="flex gap-2">
                  <FormField
                    control={form.control}
                    name="phoneCountryCode"
                    render={({ field: countryField }) => (
                      <Select onValueChange={countryField.onChange} value={countryField.value}>
                        <SelectTrigger className="w-32">
                          <SelectValue placeholder="Kodi" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="+355">🇦🇱 +355</SelectItem>
                          <SelectItem value="+383">🇽🇰 +383</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                  <FormControl>
                    <div className="flex-1 relative">
                      <Input 
                        placeholder="Numri i telefonit" 
                        {...field}
                        type="tel"
                        className="pl-10"
                      />
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <Phone className="h-4 w-4 text-gray-500" />
                      </div>
                    </div>
                  </FormControl>
                </div>
                <FormMessage />
                
                <div className="flex gap-4 mt-2">
                  <FormField
                    control={form.control}
                    name="whatsappEnabled"
                    render={({ field }) => (
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                        <label className="text-sm text-gray-700">WhatsApp</label>
                      </div>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="viberEnabled"
                    render={({ field }) => (
                      <div className="flex items-center space-x-2">
                        <Checkbox
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                        <label className="text-sm text-gray-700">Viber</label>
                      </div>
                    )}
                  />
                </div>
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Përshkrimi</FormLabel>
                <FormControl>
                  <Textarea placeholder="Përshkrimi" className="resize-none" {...field} />
                </FormControl>
                <FormMessage />
                <AIDescriptionButton
                  title={form.getValues("title")}
                  category={form.getValues("category")}
                  subcategory={form.getValues("subcategory")}
                  condition={form.getValues("condition")}
                  onGeneratedDescription={handleAIDescription}
                />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="category"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Kategoria</FormLabel>
                <Select 
                  onValueChange={(value) => {
                    field.onChange(value);
                    handleCategoryChange(value);
                  }} 
                  value={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Zgjidh një kategori" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {categoriesList.map((category: any) => (
                      <SelectItem key={category.id} value={category.id.toString()}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="subcategory"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Nënkategoria</FormLabel>
                <Select 
                  onValueChange={(value) => {
                    field.onChange(value);
                    handleSubcategoryChange(value);
                  }} 
                  value={field.value}
                  disabled={!selectedCategory || subcategoriesList.length === 0}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Zgjidh një nënkategori" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {subcategoriesList.map((subcategory: any) => (
                      <SelectItem key={subcategory.id} value={subcategory.id.toString()}>
                        {subcategory.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="price"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{isJobCategory ? 'Paga' : 'Çmimi'}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input 
                      placeholder="0.00" 
                      {...field} 
                      className="pl-8"
                    />
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <span className="text-gray-500">€</span>
                    </div>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="isPriceNegotiable"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                <FormControl>
                  <Checkbox 
                    checked={field.value} 
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>Çmim i negociueshëm</FormLabel>
                  <p className="text-sm text-muted-foreground">
                    Zgjidhni këtë opsion nëse çmimi është i hapur për negocim
                  </p>
                </div>
              </FormItem>
            )}
          />

          {!isServiceCategory && (
            <FormField
              control={form.control}
              name="condition"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Gjendja</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Zgjidh një gjendje" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="new">E re</SelectItem>
                      <SelectItem value="like_new">Si e re</SelectItem>
                      <SelectItem value="good">E mirë</SelectItem>
                      <SelectItem value="used">E përdorur</SelectItem>
                      <SelectItem value="needs_repair">Nevojitet riparim</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name="country"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Shteti</FormLabel>
                <Select onValueChange={field.onChange} value={field.value || 'albania'}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Zgjidh shtetin" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="albania">Shqipëri</SelectItem>
                    <SelectItem value="kosovo">Kosovë</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="location"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Qyteti</FormLabel>
                <FormControl>
                  <CitySelect
                    value={field.value}
                    onValueChange={field.onChange}
                    country={form.getValues("country")}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {!isServiceCategory && (
            <>
              <FormField
                control={form.control}
                name="deliveryOption"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Opsioni i dorëzimit</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Zgjidh një opsion dorëzimi" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="pickup_only">Terheqje</SelectItem>
                        <SelectItem value="shipping_and_pickup">
                          Dorëzim dhe Tërheqje
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {form.getValues("deliveryOption") === "shipping_and_pickup" && (
                <FormField
                  control={form.control}
                  name="shippingCost"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Kostoja e transportit (opsionale)</FormLabel>
                      <FormControl>
                        <Input placeholder="Kostoja e transportit" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </>
          )}
          
          {!isServiceCategory && !isJobCategory && (
            <FormField
              control={form.control}
              name="stock"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Stoku</FormLabel>
                  <FormControl>
                    <Input placeholder="Stoku" {...field} type="number" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <div className="mb-4">
            <FormLabel>Imazhe (Maksimumi {MAX_IMAGES})</FormLabel>
            <FormControl>
              <Input 
                type="file" 
                multiple 
                accept="image/*" 
                onChange={handleImageChange} 
                disabled={uploadedImageUrls.length + images.length >= MAX_IMAGES || isUploadingAssets}
              />
            </FormControl>
            <FormMessage />
            {uploadError && !uploadError.toLowerCase().includes("video") && <p className="text-red-500 text-sm mt-1">{uploadError}</p>}
            {isUploadingAssets && (
              <div className="mt-2">
                <Progress value={assetUploadProgress} className="w-full h-2" />
                <p className="text-xs text-center mt-1 text-gray-500">Duke ngarkuar skedarët...</p>
              </div>
            )}
            <div className="flex mt-2 flex-wrap gap-2">
              {uploadedImageUrls.map((url, index) => (
                <div key={`uploaded-img-${index}`} className="relative">
                  <img
                    src={url}
                    alt={`Imazhi i ngarkuar ${index + 1}`}
                    className="w-20 h-20 object-cover rounded border"
                  />
                  <button
                    type="button"
                    onClick={() => removeUploadedImage(index)}
                    disabled={isUploadingAssets}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                  >
                    ×
                  </button>
                </div>
              ))}
              {images.map((file, index) => (
                <div key={`new-img-${index}`} className="relative">
                  <img
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                    className="w-20 h-20 object-cover rounded border"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    disabled={isUploadingAssets}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          <VideoUploader
            videoFile={videoFile}
            setVideoFile={setVideoFile}
            currentVideoUrl={currentVideoUrl}
            onRemoveCurrentVideo={handleRemoveCurrentVideo}
            maxSizeMB={MAX_VIDEO_SIZE_MB}
            isUploading={isUploadingAssets && !!videoFile}
          />
           {uploadError && uploadError.toLowerCase().includes("video") && <p className="text-red-500 text-sm mt-1">{uploadError}</p>}


          <Button type="submit" disabled={isUploadingAssets || form.formState.isSubmitting}>
            {isUploadingAssets || form.formState.isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Përditëso Shpalljen
          </Button>
        </form>
      </Form>
    </div>
  );
};

export default EditListing;
